// Facility → Branches page. Legacy: src/app/[locale]/[country]/account/
// facility/[facilityId]/facilities/facility-facilities-page-client.tsx —
// feature-gated on `facility.chain.dashboard_kpi` (multi-facility / chain
// management requires Enterprise) with a teaser for lower plans, then mounts
// FacilityBranchesClient with canWrite/canDelete derived from the caller's
// facility role.
//
// SPA adaptations:
//   - FeatureAccessProvider is not mounted globally yet — mount it locally
//     around the gated content (same pattern as provider/team.tsx). Backend
//     still enforces RBAC on every request; this is UX gating only.
//   - The apps/api JWT has no facilityTeams claim — the caller's role comes
//     from the shared /api/account/workspaces cache instead.

import { useMemo } from "react";

import { Trans } from "@lingui/react/macro";
import { apiFetch } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Building2 } from "lucide-react";

import { AccountSecurityBanner } from "@/components/account/account-security-banner";
import { FacilityBranchesClient } from "@/components/account/facility/facility-branches-client";
import { FacilityFeatureTeaser } from "@/components/account/facility/facility-feature-teaser";
import {
  FeatureAccessProvider,
  useFeatureAccess,
  useFeatureAccessLoading,
} from "@/components/features/feature-access-provider";
import { Skeleton } from "@/components/ui/skeleton";
import type { OwnerType } from "@/lib/features/types";

export const Route = createFileRoute("/_authenticated/account/facility/$facilityId/facilities")({
  component: FacilityBranchesRoute,
});

// Mirrors legacy facility-facilities-page-client.tsx WRITE_ROLES/DELETE_ROLES
// (and apps/api BRANCH_WRITE_ROLES/BRANCH_DELETE_ROLES).
const WRITE_ROLES = ["owner", "director", "admin", "facility_manager"];
const DELETE_ROLES = ["owner", "director", "admin"];

// Stable reference — feeds FeatureAccessProvider's memoized scope key.
const BRANCHES_PAGE_SCOPES: OwnerType[] = ["user", "facility"];

const WORKSPACES_KEY = ["api", "/api/account/workspaces"] as const;

interface WorkspaceLite {
  type: string;
  id: string;
  role: string;
}

function FacilityBranchesRoute() {
  const { facilityId } = Route.useParams();

  const ownerIds = useMemo(() => ({ facility: facilityId }), [facilityId]);

  return (
    <FeatureAccessProvider activeScopes={BRANCHES_PAGE_SCOPES} ownerIds={ownerIds}>
      <GatedFacilityBranches facilityId={facilityId} />
      <AccountSecurityBanner />
    </FeatureAccessProvider>
  );
}

function GatedFacilityBranches({ facilityId }: { facilityId: string }) {
  // Multi-facility / chain management requires Enterprise (legacy gate).
  const access = useFeatureAccess("facility.chain.dashboard_kpi");
  const accessLoading = useFeatureAccessLoading();

  // Caller's facility role — legacy read session.user.facilityTeams; the SPA
  // resolves it from the workspaces list (shared query cache).
  const workspacesQ = useQuery({
    queryKey: WORKSPACES_KEY,
    queryFn: () => apiFetch.get<{ workspaces: WorkspaceLite[] }>("/api/account/workspaces"),
    staleTime: 60_000,
    retry: false,
  });

  if (accessLoading || !access || workspacesQ.isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-40" />
        <Skeleton className="h-24 w-full rounded-xl" />
        <Skeleton className="h-24 w-full rounded-xl" />
      </div>
    );
  }

  const allowed =
    access.access === "included" || (access.access === "limited" && !access.exhausted);

  if (!allowed) {
    return <BranchesFeatureTeaser facilityId={facilityId} />;
  }

  const role =
    workspacesQ.data?.workspaces.find((w) => w.type === "facility" && w.id === facilityId)?.role ??
    "";
  const canWrite = WRITE_ROLES.includes(role);
  const canDelete = DELETE_ROLES.includes(role);

  return (
    <FacilityBranchesClient facilityId={facilityId} canWrite={canWrite} canDelete={canDelete} />
  );
}

// ─── Feature teaser (plan without chain management) ─────────────────────────
//
// Branch management and the network KPI dashboard (/account/network) exist;
// branch brand enforcement and centralized billing/reports do not yet.

function BranchesFeatureTeaser({ facilityId }: { facilityId: string }) {
  return (
    <FacilityFeatureTeaser
      facilityId={facilityId}
      pageTitle={<Trans>Branches</Trans>}
      icon={Building2}
      title={<Trans>Multi-facility / chain management</Trans>}
      bullets={[
        { label: <Trans>Manage branches (HQ + locations) from one place</Trans> },
        { label: <Trans>Aggregated KPI dashboard across all locations</Trans> },
        {
          label: <Trans>Brand consistency (logo, colors) enforced on branches</Trans>,
          soon: true,
        },
        { label: <Trans>Centralized billing + consolidated reports</Trans>, soon: true },
      ]}
    />
  );
}
